import { z } from 'zod';
import {
  PositiveDecimalString,
  NonNegativeDecimalString,
  PaginationQuerySchema,
} from './common.js';

/**
 * UOC sub-system schemas — admin config CRUD + user mint/burn + admin
 * recharge + log queries.
 *
 * UOC (源石币) is a stablecoin that acts as a conversion hub:
 *   - Mint: EP1/EP2 → UOC (1:1, uses existing internal convert)
 *   - Burn: UOC → RP/TP/EP (rate-controlled via UocConfig)
 *   - Recharge: admin adds UOC supply to the system pool
 *
 * The UocConfig table has one row per target token (rp, tp, ep), each
 * controlling pool_balance, rate, and is_active for that conversion path.
 */

// ---- UOC Config (admin CRUD) -----------------------------------------------

const UOC_TARGET_TOKENS = ['rp', 'tp', 'ep'] as const;

export const UocConfigCreateSchema = z.object({
  target_token: z.enum(UOC_TARGET_TOKENS),
  pool_balance: NonNegativeDecimalString,
  rate: PositiveDecimalString,
  is_active: z.coerce.boolean().default(true),
  note: z.string().max(500).optional(),
});
export type UocConfigCreateRequest = z.infer<typeof UocConfigCreateSchema>;

export const UocConfigPatchSchema = z.object({
  pool_balance: NonNegativeDecimalString.optional(),
  rate: PositiveDecimalString.optional(),
  is_active: z.coerce.boolean().optional(),
  note: z.string().max(500).optional(),
});
export type UocConfigPatchRequest = z.infer<typeof UocConfigPatchSchema>;

export const UocConfigListQuerySchema = PaginationQuerySchema.extend({
  is_active: z
    .enum(['true', 'false'])
    .optional()
    .transform((v) => (v === undefined ? undefined : v === 'true')),
});
export type UocConfigListQuery = z.infer<typeof UocConfigListQuerySchema>;

// ---- UOC Admin Recharge ----------------------------------------------------

/** Admin recharges UOC supply — bumps UocSupply.total_supply + current_supply. */
export const UocRechargeSchema = z.object({
  amount: PositiveDecimalString,
  note: z.string().max(500).optional(),
});
export type UocRechargeRequest = z.infer<typeof UocRechargeSchema>;

// ---- UOC User Mint (EP1/EP2 → UOC) ----------------------------------------

export const UocMintSchema = z.object({
  /** Source token: 'ep1' or 'ep2'. */
  from_asset: z.enum(['ep1', 'ep2']),
  /** Amount of source token to convert. Minted UOC = amount × 1 (1:1). */
  amount: PositiveDecimalString,
  idempotency_key: z.string().uuid().optional(),
});
export type UocMintRequest = z.infer<typeof UocMintSchema>;

// ---- UOC User Burn (UOC → RP/TP/EP) ---------------------------------------

export const UocBurnSchema = z.object({
  /** Target token: 'rp', 'tp', or 'ep'. */
  to_asset: z.enum(UOC_TARGET_TOKENS),
  /** Amount of UOC to burn. Received target = amount × config.rate. */
  amount: PositiveDecimalString,
  idempotency_key: z.string().uuid().optional(),
});
export type UocBurnRequest = z.infer<typeof UocBurnSchema>;

// ---- Log query (admin) -----------------------------------------------------

export const UocLogQuerySchema = PaginationQuerySchema.extend({
  /** Filter by action: 'uoc_mint' | 'uoc_burn' | 'uoc_admin_recharge'. */
  action: z
    .enum(['uoc_mint', 'uoc_burn', 'uoc_admin_recharge'])
    .optional(),
  user_id: z.coerce.number().int().positive().optional(),
});
export type UocLogQuery = z.infer<typeof UocLogQuerySchema>;
